import { useSyncExternalStore } from "react";

/**
 * ═══════════════════════════════════════════════════════════════
 *  当前身份（买家 / 哪一家店的经营者）
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **一个人同时是买家和经营者。**「我的」这一页按这个值整页换内容 ——
 *    切到买家看订单地址，切到某家店看经营入口。
 *
 * ⚠️ 空串 = 买家（我自己）。
 * ⚠️ 存 localStorage：天天做生意的人，每次进来都退回买家的话要多点一下。
 *    读写都可能抛（隐私模式、禁用站点数据），一律当成"没选过"，
 *    绝不能让整页崩掉。
 */
const KEY = "zx.me.identity";

/**
 * 🔴 **身份变了要能通知到全局。**
 *    价格视角是在根组件上算的（每个页面都要用），而切换身份发生在「我的」页。
 *    不广播的话，切完要等下一次刷新才生效 —— 用户会以为点了没反应。
 *
 * ⚠️ `storage` 事件只在**别的标签页**改动时触发，同一个页面自己写是收不到的，
 *    所以必须自己发一个。
 */
const CHANGED = "zx:identity-changed";

export function notifyIdentityChanged(): void {
  window.dispatchEvent(new Event(CHANGED));
}

export function onIdentityChanged(fn: () => void): () => void {
  window.addEventListener(CHANGED, fn);
  return () => window.removeEventListener(CHANGED, fn);
}

/**
 * @returns `null` = **他从来没选过**（可以替他默认到自己的店）；
 *          `""` = 他**明确选了买家**；`"<orgId>"` = 某一家店。
 *
 * 🔴 **"没选过"和"选了买家"必须分得开。**
 *    两个都当成空串的话，自动默认那段会在他每次切回买家的瞬间
 *    又把他推回商家身份 —— 表现就是**永远切不回去**，点了没反应。
 */
export function readIdentity(): string | null {
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

export function writeIdentity(v: string): void {
  try {
    localStorage.setItem(KEY, v);
  } catch {
    /* 存不下就算了，只是下次要重选一次 */
  }
  notifyIdentityChanged();
}

/**
 * 记住"他此刻在经营哪一家店"。
 *
 * 🔴 **直接打开商家的二级页时必须调这个**（书签、二维码、别人转发的链接）。
 *    不调的话，他从「我的定价」按返回会落到**买家视角的「我的」**——
 *    刚才还在改价，一返回变成了订单和收货地址，还得自己再切一次身份。
 */
export function rememberOrg(orgId: number | undefined | null): void {
  if (orgId) writeIdentity(String(orgId));
}

/**
 * ═══════════════════════════════════════════════════════════════
 *  采购身份
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **有上级的商家逛商城，看到的就该是上级给他的进货价。**
 *
 *    王师傅有上级（经销商A）。他登录之后逛首页却看到**平台指导零售价** ——
 *    那是给没有归属的陌生人看的最贵一档，他这辈子都不会按那个价买。
 *    于是首页对他毫无意义：看得到商品，却要另外去一个纯文字的「进货」页
 *    才能按真实的价下单。老板的原话：「那我这个首页又有什么意义呢？」
 *
 *    现在：切到商家身份 → **整个商城按他的进货价显示**，图片、分类、搜索、
 *    购物车、结算全都照常用。进货不再是一个另开的残缺列表。
 *
 * ⚠️ 跟 `X-Org-Id` 是两回事：那个表示"我在管理这个商家"，
 *    这个表示"我在替这个商家进货"。
 *
 * ⚠️ 只有**经销商 / 分销商**能进货：平台上面没有上级（它拿的是供货价，
 *    不该在前台默认按那个价显示），供应商是供货方、根本不进货。
 */
const PURCHASE_KEY = "zx.me.purchaseAs";

export function readPurchaseAs(): number | null {
  try {
    const v = Number(localStorage.getItem(PURCHASE_KEY));
    return Number.isInteger(v) && v > 0 ? v : null;
  } catch {
    return null;
  }
}

export function writePurchaseAs(orgId: number | null): void {
  try {
    if (orgId) localStorage.setItem(PURCHASE_KEY, String(orgId));
    else localStorage.removeItem(PURCHASE_KEY);
  } catch {
    /* 存不下就按普通消费者逛，不影响能不能用 */
  }
  notifyIdentityChanged();
}

/**
 * 这种身份能不能进货。
 *
 * 🔴 **平台也算。** 陈总登录之后看到的绝不该是平台指导零售价 ——
 *    那是给自己注册的陌生消费者看的最贵一档。他的上级是供应商，
 *    他看到的应该是**供货价**（真机上是 ¥2980，零售价 ¥6680）。
 *    这个平台里每一级都既买又卖，平台自己也是从供应商那里进货的。
 *
 * ⚠️ 供应商不算：他是供货方，货就是他自己出的，不存在"进货"。
 */
export function canPurchaseAs(type: string): boolean {
  return type === "dealer" || type === "distributor" || type === "platform";
}

/**
 * ═══════════════════════════════════════════════════════════════
 *  看价的视角
 * ═══════════════════════════════════════════════════════════════
 *
 * 一个商家身份下有两个完全不同的价：
 *
 * - `purchase`（默认）**我买的价** —— 上级报给我的进货价
 * - `shop`                **我卖的价** —— 客户在我店里看到的价
 *
 * 🔴 定完价之后最想做的一件事就是"看看客户那边长什么样"。
 *    以前只能去自己的店铺页零散地看，整个商城仍然按进货价显示。
 *
 * ⚠️ `shop` 是**只读预览**：按那个价下单会生成一张"自己卖给自己"的单，
 *    而且跟服务端按真实身份算出的价对不上，会被 `PRICE_CHANGED` 拦下。
 */
export type ViewAs = "purchase" | "shop";

const VIEW_KEY = "zx.me.viewAs";

export function readViewAs(): ViewAs {
  try {
    return localStorage.getItem(VIEW_KEY) === "shop" ? "shop" : "purchase";
  } catch {
    return "purchase";
  }
}

export function writeViewAs(v: ViewAs): void {
  try {
    localStorage.setItem(VIEW_KEY, v);
  } catch {
    /* 存不下就每次默认按进货价看 */
  }
  notifyIdentityChanged();
}

/**
 * 根据「他选了哪个身份」算出**这一刻该按什么价看**。
 *
 * 🔴 **这是唯一的推导处。** 以前只有「我的」那一页在算 ——
 *    于是登录后直接落在**首页**的人，采购身份根本没被写过，
 *    有上级的商家一进来看到的是**平台指导价**（给没有上级的陌生人的最贵一档）。
 *    现在挪到根组件，哪个页面先打开都一样。
 */
export function derivePriceView(
  orgs: readonly { id: number; type: string }[],
  identity: string | null,
): { purchaseAs: number | null; viewAs: ViewAs } {
  const activeOrg = orgs.find((o) => String(o.id) === identity) ?? null;

  /** 选中了某家店 → 看客户在这家店看到的价。 */
  if (activeOrg) {
    return {
      purchaseAs: canPurchaseAs(activeOrg.type) ? activeOrg.id : null,
      viewAs: "shop",
    };
  }

  /**
   * 「我自己」→ 按**我的进货价**看（上一级给我的）。
   * ⚠️ 没有任何店铺的人才是真正的陌生消费者，那时按平台指导价。
   * ⚠️ 开了好几家店时按**第一家**算 —— 进货价取决于挂在谁下面，只能选一个。
   */
  return { purchaseAs: orgs.find((o) => canPurchaseAs(o.type))?.id ?? null, viewAs: "purchase" };
}

/**
 * 当前价格视角的**指纹**，用来放进查询缓存的 key。
 *
 * 🔴 **价格视角是这些请求的输入，所以它必须进 key。**
 *    早先靠"视角变了就 invalidate 一遍"来刷新 —— 那依赖**时序**：
 *    首页的商品请求在身份算出来之前就发出去了，等身份到位再作废重取，
 *    中间任何一步错位，页面上就停在旧价格（真机上就是这样：
 *    横幅已经写着"进货价"，下面的数字还是平台指导价 ¥7280）。
 *
 *    进了 key 之后就跟时序无关了：不同的视角天然是不同的缓存条目，
 *    视角一变，组件重渲染、用新 key 取数，旧数据也不会被误用。
 *
 * ⚠️ 用 `useSyncExternalStore` 订阅身份变更事件 —— 这个值存在 localStorage 里，
 *    不是 React state，光靠 `readXxx()` 读是不会触发重渲染的。
 */
export function usePriceView(): string {
  return useSyncExternalStore(
    onIdentityChanged,
    () => `${readPurchaseAs() ?? 0}:${readViewAs()}`,
    /** ⚠️ 服务端渲染/预渲染时没有 localStorage，给个稳定的默认值。 */
    () => "0:purchase",
  );
}
